import React from 'react';
import {connect} from 'react-redux';
import {ToggleSwitchButton} from '../../../toggle-switch-button';
import {updateFileSize, updateMarginFlag} from '../../download-publish-modal-reducer';
import {Dropdown} from '../../../dropdown';
import './download-more-options.scss';
import {
  ANALYTICS_DOWNLOAD_CATEGORY,
  FILE_FORMAT_GIF,
  FILE_FORMAT_MP4,
  FILE_FORMAT_PDF,
  FILE_QUALITY_HIGH,
  FILE_QUALITY_LOW,
  FILE_QUALITY_MEDIUM,
  FILE_QUALITY_PHOTO,
  FILE_QUALITY_WEB,
  FILE_SIZE_LARGE,
  getDimensionsForGivenPrintSizeQuality,
  getDimensionsForGivenSocialMediaSizeQuality,
  getDimensionsForGivenVideoQuality,
  isPDFFormatSelected,
  PRODUCT_STATUS_DOWNLOADABLE,
  PRODUCT_STATUS_FREEBIE,
} from '../../../../libraries/download-panel-library';
import PropTypes from 'prop-types';
import {MoreOptionItem} from '../more-option-item';
import {SlideDown} from 'react-slidedown';
import 'react-slidedown/lib/slidedown.css';
import {BleedOption} from '../bleed-option';

/**
 * Renders the 'Details..' section in download panel
 *
 * @author <ahsan@250mils.com>
 */
export class DownloadMoreOptions extends React.Component {
  /**
   * @type {string}
   */
  static FORMAT_LETTER_PDF_STRETCHED = 'letter-pdf-s';
  static FORMAT_LETTER_PDF_FTP = 'letter-pdf-ftp';
  static FORMAT_SLICED_PDF = 'slice-pdf';

  constructor(props) {
    super(props);
    this.state = {
      expandOptions: this.props.expandOptions,
      onMoreOptionsClickCallback: this.props.onMoreOptionsClickCallback,
      selectedSize: this.#getDefaultFileSize(),
    };
  }

  componentDidMount() {
    this.#setFileSize();
  }

  render = () => {
    return (
      <div className={`more-options-container`}>
        <div className={'options-header'} onClick={this.#toggleDetailsSection}>
          <div className={'flex-h-row'}>
            <i className={'icon-sliders icon'} />
            <span className={`body-s-bold title ${!this.state.expandOptions ? 'add-s' : ''}`}>{i18next.t('pmwjs_details')}</span>
            {this.#getDetailsSectionHeading()}
          </div>
          <i className={`icon ${this.state.expandOptions ? 'icon-caret-up' : 'icon-caret-down'}`} />
        </div>
        <SlideDown className={'details-options-slider'}>{this.state.expandOptions && this.#getMarkupForAvailableOptions()}</SlideDown>
      </div>
    );
  };

  /**
   *
   * @returns {JSX.Element}
   */
  #getMarkupForAvailableOptions = () => {
    return (
      <div className={'product-details spacing-p-3'}>
        {this.#showDisplaydimensions() && <MoreOptionItem name={i18next.t('pmwjs_dimensions')} value={this.#getDefaultDimensionsForSelectedProduct()} />}
        {this.#doShowVideoFileSizeDropdown() && <MoreOptionItem name={i18next.t('pmwjs_file_size')} value={this.#getDropDownMarkupForVideoFileSize()} />}
        {this.#doShowWebFileSizeDropdown() && <MoreOptionItem name={i18next.t('pmwjs_file_size')} value={this.#getDropDownMarkupForImageFileSize()} />}
        {this.#doShowPrintFileSizeDropdown() && <MoreOptionItem name={i18next.t('pmwjs_file_size')} value={this.#getDropDownMarkupForPrintImageFileSize()} />}
        {this.#doShowBleedOption() && <MoreOptionItem name={i18next.t('pmwjs_bleed')} value={<BleedOption />} />}
        {this.#doShowMarginOption() && (
          <MoreOptionItem name={i18next.t('pmwjs_has_margin')} value={<ToggleSwitchButton isChecked={false} onChangeHandler={this.#onMarginToggle} />} />
        )}
        {this.#showMoreDownloadOptionsText() && (
          <div className={'detail-item -spacing-top'}>
            <div className={'more-options-link'}>
              <i className={'icon-info content-sub-text body-l'} />
              <span className={'body-xs content-sub-text'} onClick={this.state.onMoreOptionsClickCallback} dangerouslySetInnerHTML={{__html: this.#getTextForMoreDownloadOptions()}} />
            </div>
          </div>
        )}
      </div>
    );
  };

  #getTextForMoreDownloadOptions = () => {
    let selectedProduct = this.props.selectedProduct;
    if (selectedProduct.downloadUrl && selectedProduct.status === PRODUCT_STATUS_DOWNLOADABLE) {
      return i18next.t('pmwjs_more_download_options_link');
    } else {
      return i18next.t('pmwjs_more_download_options');
    }
  };

  /**
   * Check to display dimensions alone
   * Returns true if file size dropdown is not shown for the selected product
   * @returns {boolean}
   */
  #showDisplaydimensions = () => {
    return !(this.#doShowVideoFileSizeDropdown() || this.#doShowWebFileSizeDropdown() || this.#doShowPrintFileSizeDropdown());
  };

  /**
   * @returns {string}
   */
  #getDefaultDimensionsForSelectedProduct = () => {
    if (this.props.selectedProduct.height && this.props.selectedProduct.width) {
      return (this.props.selectedProduct.width + ' x ' + this.props.selectedProduct.height + ' px').toString();
    } else {
      console.error('Product dimensions not found');
      return '';
    }
  };

  /**
   * Updates the Toggle value in the download panel reducer
   * @param {String} val
   */
  #onMarginToggle = (val) => {
    PMW.redux.store.dispatch(updateMarginFlag(val));
  };

  /**
   * Updates the File Size in the download panel reducer
   * @param {String} size
   */
  #onSelectFileSize = (size) => {
    PMW.redux.store.dispatch(updateFileSize(size));
    this.state.selectedSize = size;
  };

  /**
   * Returns true if Margin option is applicable on the selected product, false otherwise
   * @returns {boolean}
   */
  #doShowMarginOption = () => {
    const showMargin =
      !this.props.selectedProduct.isWebDownload && !this.props.selectedProduct.isVideo && this.#isFormatPDF() && !(this.props.selectedProduct.status === PRODUCT_STATUS_FREEBIE);
    if (!showMargin) {
      PMW.redux.store.dispatch(updateMarginFlag(false));
    }
    return (
      !this.props.selectedProduct.isWebDownload && !this.props.selectedProduct.isVideo && this.#isFormatPDF() && !(this.props.selectedProduct.status === PRODUCT_STATUS_FREEBIE)
    );
  };

  /**
   * @returns {boolean}
   */
  #isFormatPDF = () => {
    return (
      this.props.selectedFormat === FILE_FORMAT_PDF ||
      this.props.selectedFormat === DownloadMoreOptions.FORMAT_LETTER_PDF_STRETCHED ||
      this.props.selectedFormat === DownloadMoreOptions.FORMAT_LETTER_PDF_FTP
    );
  };

  /**
   * @returns {boolean}
   */
  #doShowBleedOption = () => {
    return (
      !this.props.selectedProduct.isWebDownload &&
      !this.props.selectedProduct.isVideo &&
      !(this.props.selectedFormat === DownloadMoreOptions.FORMAT_SLICED_PDF) &&
      !(this.props.selectedProduct.status === PRODUCT_STATUS_FREEBIE)
    );
  };

  /**
   * @returns {boolean}
   */
  #doShowVideoFileSizeDropdown = () => {
    return this.props.selectedFormat === FILE_FORMAT_MP4 || this.props.selectedFormat === FILE_FORMAT_GIF;
  };

  /**
   * @returns {boolean}
   */
  #doShowWebFileSizeDropdown = () => {
    return this.props.selectedProduct.isWebDownload;
  };

  /**
   * @returns {boolean}
   */
  #doShowPrintFileSizeDropdown = () => {
    return !this.props.selectedProduct.isWebDownload && !this.props.selectedProduct.isVideo && !(this.props.selectedProduct.status === PRODUCT_STATUS_FREEBIE);
  };

  /**
   * Returns true if the selected product is not freebie, no need to show the download options text for freebie downloads
   * @returns {boolean}
   */
  #showMoreDownloadOptionsText = () => {
    return !(this.props.selectedProduct.status === PRODUCT_STATUS_FREEBIE);
  };

  #getDefaultFileSize = () => {
    let fileSize = FILE_SIZE_LARGE;
    if (this.props.selectedProduct.isVideo) {
      fileSize = FILE_QUALITY_HIGH;
    } else if (this.props.selectedProduct.isWebDownload) {
      fileSize = FILE_QUALITY_WEB;
    }
    return fileSize;
  };

  #setFileSize = () => {
    let fileSize = this.state.selectedSize ? this.state.selectedSize : this.#getDefaultFileSize();
    PMW.redux.store.dispatch(updateFileSize(fileSize));
  };

  /**
   *
   * @return JSX.Element
   */
  #getDetailsSectionHeading = () => {
    let heading = '';
    if (this.props.selectedProduct.isVideo || this.props.selectedProduct.isWebDownload || this.props.selectedFormat === DownloadMoreOptions.FORMAT_SLICED_PDF) {
      heading = i18next.t('pmwjs_size');
    } else if (this.props.selectedProduct.isFreebie) {
      heading = i18next.t('pmwjs_dimensions');
    } else {
      heading = i18next.t('pmwjs_size_and_more');
    }
    return <span className={`body-s add-stop details-sub-heading ${this.state.expandOptions ? '-hide' : '-show'}`}>{heading}</span>;
  };

  #toggleDetailsSection = () => {
    this.setState(
      (prevState) => ({
        expandOptions: !prevState.expandOptions,
      })
    );
  };

  /**
   *
   * @return {JSX.Element}
   */
  #getDropDownMarkupForImageFileSize = () => {
    let options = [],
      dimensions = this.#getSelectedProductDimensions(),
      hideDimensions = isPDFFormatSelected();
    options.push(
      {
        label: hideDimensions ? i18next.t('pmwjs_small') : i18next.t('pmwjs_small_x', {dimensions: getDimensionsForGivenSocialMediaSizeQuality(FILE_QUALITY_WEB, dimensions)}),
        dataValue: FILE_QUALITY_WEB,
      },
      {
        label: hideDimensions ? i18next.t('pmwjs_large') : i18next.t('pmwjs_large_x', {dimensions: getDimensionsForGivenSocialMediaSizeQuality(FILE_QUALITY_PHOTO, dimensions)}),
        dataValue: FILE_QUALITY_PHOTO,
      }
    );

    return (
      <Dropdown
        dropdownItems={options}
        dropdownClasses={`-primary -with-border -file-size ${hideDimensions ? '-small' : ''}`}
        dropdownItemClasses={'body-xs'}
        dropdownLabelClasses={'file-size-opts body-xs'}
        clickHandler={this.#onSelectFileSize}
        defaultDataValue={this.#getDefaultSizeForImage()}
        title={i18next.t('pmwjs_file_size')}
        portalDomId={'download-panel-dropdown-portal'}
        aria-label={i18next.t('pmwjs_size')}
        showDropdownAsModalForMobile={true}
        dropdownItemMobileClasses='spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4'
      />
    );
  };

  #getDefaultSizeForImage = () => {
    if (this.state.selectedSize && (this.state.selectedSize === FILE_QUALITY_WEB || this.state.selectedSize === FILE_QUALITY_PHOTO)) {
      return this.state.selectedSize;
    }
    return FILE_QUALITY_WEB;
  };

  /**
   *
   * @return {JSX.Element}
   */
  #getDropDownMarkupForPrintImageFileSize = () => {
    let options = [],
      dimensions = this.#getSelectedProductDimensions(),
      hideDimensions = isPDFFormatSelected();
    options.push(
      {
        label: hideDimensions ? i18next.t('pmwjs_small') : i18next.t('pmwjs_small_x', {dimensions: getDimensionsForGivenPrintSizeQuality(FILE_QUALITY_WEB, dimensions)}),
        dataValue: FILE_QUALITY_WEB,
      },
      {
        label: hideDimensions ? i18next.t('pmwjs_large') : i18next.t('pmwjs_large_x', {dimensions: getDimensionsForGivenPrintSizeQuality(FILE_SIZE_LARGE, dimensions)}),
        dataValue: FILE_SIZE_LARGE,
      }
    );

    return (
      <Dropdown
        dropdownItems={options}
        dropdownClasses={`-primary -with-border -file-size ${hideDimensions ? '-small' : ''}`}
        dropdownItemClasses={'body-xs'}
        dropdownLabelClasses={'file-size-opts body-xs'}
        clickHandler={this.#onSelectFileSize}
        defaultDataValue={this.#getDefaultSizeForPrintImage()}
        title={i18next.t('pmwjs_file_size')}
        portalDomId={'download-panel-dropdown-portal'}
        aria-label={i18next.t('pmwjs_size')}
        showDropdownAsModalForMobile={true}
        dropdownItemMobileClasses='spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4'
      />
    );
  };

  #getDefaultSizeForPrintImage = () => {
    if (this.state.selectedSize && (this.state.selectedSize === FILE_QUALITY_WEB || this.state.selectedSize === FILE_SIZE_LARGE)) {
      return this.state.selectedSize;
    }
    return FILE_SIZE_LARGE;
  };

  /**
   * @returns {{width: number, height: number}}
   */
  #getSelectedProductDimensions = () => {
    return {
      width: parseInt(this.props.selectedProduct.width),
      height: parseInt(this.props.selectedProduct.height),
    };
  };

  /**
   *
   * @return {JSX.Element}
   */
  #getDropDownMarkupForVideoFileSize = () => {
    let options = [],
      dimensions = this.#getSelectedProductDimensions();

    options.push(
      {
        label: i18next.t('pmwjs_large_x', {dimensions: getDimensionsForGivenVideoQuality(this.props.selectedFormat, FILE_QUALITY_HIGH, dimensions)}),
        dataValue: FILE_QUALITY_HIGH,
      },
      {
        label: i18next.t('pmwjs_medium_x', {dimensions: getDimensionsForGivenVideoQuality(this.props.selectedFormat, FILE_QUALITY_MEDIUM, dimensions)}),
        dataValue: FILE_QUALITY_MEDIUM,
      },
      {
        label: i18next.t('pmwjs_small_x', {dimensions: getDimensionsForGivenVideoQuality(this.props.selectedFormat, FILE_QUALITY_LOW, dimensions)}),
        dataValue: FILE_QUALITY_LOW,
      }
    );

    return (
      <Dropdown
        dropdownItems={options}
        dropdownClasses={'-primary -with-border -file-size'}
        dropdownItemClasses={'body-xs'}
        dropdownLabelClasses={'file-size-opts body-xs'}
        clickHandler={this.#onSelectFileSize}
        defaultDataValue={this.#getDefaultSizeForVideo()}
        title={i18next.t('pmwjs_file_size')}
        portalDomId={'download-panel-dropdown-portal'}
        aria-label={i18next.t('pmwjs_size')}
        showDropdownAsModalForMobile={true}
        dropdownItemMobileClasses='spacing-p-t-3 spacing-p-b-3 spacing-p-r-4 spacing-p-l-4'
      />
    );
  };

  #getDefaultSizeForVideo = () => {
    if (
      this.state.selectedSize &&
      (this.state.selectedSize === FILE_QUALITY_HIGH || this.state.selectedSize === FILE_QUALITY_MEDIUM || this.state.selectedSize === FILE_QUALITY_LOW)
    ) {
      return this.state.selectedSize;
    }
    return FILE_QUALITY_HIGH;
  };
}

const mapDownloadMoreOptionsStateToProps = (state) => {
  return {
    selectedProduct: state.downloadPanel.selectedProduct,
    formatList: state.downloadPanel.formatList,
    selectedFormat: state.downloadPanel.selectedFormat,
  };
};

DownloadMoreOptions.propTypes = {
  /**
   * Tells whether to open the options by default or not
   */
  expandOptions: PropTypes.bool.isRequired,
  onMoreOptionsClickCallback: PropTypes.func,
};

//codeReviewAhsan: you can send a mapDispatchToProps as a second argument to connect as well and
// get all your dispatch functions available as props instead of doing PMW.store.redux.dispatch()

export default connect(mapDownloadMoreOptionsStateToProps)(DownloadMoreOptions);
